import MapView from '@arcgis/core/views/MapView';
import { debugLog, LayerState } from './utils/layer-change-coordinator';
import arcGisConnection from '../../../arcGIS/arcgis-connection';
import { Layer } from '../../../models/layer-settings';
import { MapPlugin } from '../arcgis-types';

/*
 * PortalLayers is responsible for managing AGOL and Enterprise Layers, which require
 * an active ArcGisConnection (src/arcGIS/arcgis-connection.ts).
 *
 * Unlike other Layers, these can not be created using the "new" keyword,
 * and are instead fetched from a remote "portal". Because the returned Layers
 * are wrapped in a Promise, we can not name them in the same way as other Layers.
 * Instead, we have to use the "url" to find layers this plugin controls. Furthermore,
 * because adding a promise to the map does not cause the layer to be immediately
 * accessible, removing and readding these layers can cause timing issues, leaving
 * removed layers visible on the map, even if they are not selected in the LayerPicker.
 * To get around this, Portal Layers are not removed from the map, but instead
 * hidden when removed, and then shown when added (after the initial load).
 */
const PortalLayers = (layerChangeCoordinator: LayerState, esriApiKey: string): MapPlugin => {
  // A "fulcrumLayer" is a definition from LayerSettings, while "mapLayer" is an ArcGIS layer from the map
  const isMatchingGroupLayer = (mapLayer: any, fulcrumLayer: any) => {
    if (mapLayer.type !== 'group' || !mapLayer.layers?.items) {
      return false;
    }

    const { url, id } = fulcrumLayer;

    return mapLayer.layers.items.some((layer) => layer.url === url && layer.portalItem?.id === id);
  };

  const isPortalFulcrumLayer = (fulcrumLayer: Layer) => {
    const { type, origin, url } = fulcrumLayer;
    return type === 'feature-service' && origin === 'arcgis' && url;
  };

  const isMatchingPortalLayer = (mapLayer: any, fulcrumLayer: Layer) => {
    const urlsMatch = !mapLayer.portalItem && mapLayer.url === fulcrumLayer.url;
    const isSamePortalItem = mapLayer.portalItem && mapLayer.portalItem?.id === fulcrumLayer.id;

    return urlsMatch || isSamePortalItem || isMatchingGroupLayer(mapLayer, fulcrumLayer);
  };

  return {
    extendMap: (mapView: MapView) => {
      const stillSelected = (layer: Layer) => (
        layerChangeCoordinator.activeOverlays().some((activeLayer) => activeLayer.id === layer.id)
      );

      // If a Layer is opened, and then hidden in a modal (RecordEditor/RepeatableListView), when that same Layer is
      // added to another MapView (DataViewer), it will retain `visible = false`. We can't set the visible value before
      // the Layer is added to the map (since it's retrieved as a promise), so we run this to ensure selected Portal Layers
      // in the Map are visible.
      const resetMapLayerVisibilityIfFound = (layer: Layer) => (
        mapView.map.layers.some((mapLayer) => {
          if (isMatchingPortalLayer(mapLayer, layer)) {
            if (!mapLayer.visible) {
              debugLog('Resetting visibility of layer', layer);
              mapLayer.set('visible', true);
            }
            return true;
          }
          return false;
        })
      );

      const ensureVisible = (layer: Layer) => {
        const retryDelay = 500;
        const maxRetries = 4; // 4 retries * 500ms = 2 seconds
        let retries = 0;

        const checkLayer = () => {
          if (stillSelected(layer)) {
            debugLog('Checking visibility of layer', layer);
            const found = resetMapLayerVisibilityIfFound(layer);

            if (!found) {
              if (retries < maxRetries) {
                retries += 1;
                setTimeout(checkLayer, retryDelay);
              } else {
                console.error('Could not find layer to ensure it\'s visibility. Try deselecting and reselecting the layer.', layer.name);
              }
            }
          }
        };

        checkLayer();
      };

      const loadLayer = (index: number, layer: Layer) => {
        if (isPortalFulcrumLayer(layer)) {
          const {
            id, esriAppId, esriPortalUrl, baseURL,
          } = layer;
          try {
            const connection = arcGisConnection(esriApiKey, esriAppId, esriPortalUrl);
            const arcGisLayer = connection.fetchLayer(id, baseURL);

            debugLog('Adding portal layer', index, layer);
            mapView.map.add(arcGisLayer, index);
            ensureVisible(layer);
          } catch (error) {
            console.warn('Portal layers fail to load on startup:', error);
          }
        }
      };

      const removeLayer = (layer: Layer) => {
        if (isPortalFulcrumLayer(layer)) {
          mapView.map.layers.some((mapLayer) => {
            if (isMatchingPortalLayer(mapLayer, layer)) {
              debugLog('Removing portal layer', layer);
              mapLayer.set('visible', false);
              return true;
            }
            return false;
          });
        }
      };

      const addLayer = (index: number, layer: Layer) => {
        if (isPortalFulcrumLayer(layer)) {
          const existingLayer = mapView.map.layers.find(
            (mapLayer) => (isMatchingPortalLayer(mapLayer, layer)),
          );
          if (existingLayer) {
            existingLayer.set('visible', true);
            debugLog('Showing existing portal layer', layer);
            mapView.map.reorder(existingLayer, index);
          } else {
            loadLayer(index, layer);
          }
        }
      };

      let index = 0;
      layerChangeCoordinator.activeOverlays().forEach((layer) => {
        // This represents what an initial load might look like for Portal layers,
        // however LayerSettings won't have any right now, as they must be added through the
        // LayerPicker right now. See https://fulcrumapp.atlassian.net/browse/FLCRM-15931
        loadLayer(index, layer);
        index++;
      });

      layerChangeCoordinator.onLayerChange({ remove: removeLayer, add: addLayer });
    },
  };
};

export default PortalLayers;
